import React, { useEffect, useState } from 'react';
import { Button, Form, Grid, Segment, Dropdown, Label, Table, Container, Header, Message, Loader, Icon, Modal } from 'semantic-ui-react';
import { fetchStores, fetchEmployees, mapStoresToDropdown, mapEmployeesToDropdown, getBorrowedEmployees, submitBorrowEmployee, deleteBorrowedEmployee, BorrowedEmployee, Store } from '../api/BorrowEmployee';
import './BorrowEmployee.css';

const BorrowingForm = () => {
	const [storeOptions, setStoreOptions] = useState<any[]>([]); // Stores dropdown options
	const [selectedStore, setSelectedStore] = useState<string>(''); // Selected store
	const [market, setMarket] = useState<string>(''); // Market name
	const [userLogInStore, setuserLogInStore] = useState<string>(''); // userLogInStore
	const [loadingStores, setLoadingStores] = useState<boolean>(true); // Loading state for stores
	const [employee, setEmployee] = useState<string>(''); // Selected employee
	const [startDate, setStartDate] = useState<string | null>(null); // Set start date to null initially
	const [endDate, setEndDate] = useState<string | null>(null); // Set end date to null initially
	const [errorMessage, setErrorMessage] = useState<string | null>(null);
	const [statusMessage, setStatusMessage] = useState<string | null>(null);
	const [borrowedEmployees, setBorrowedEmployees] = useState<BorrowedEmployee[]>([]);
	const [employeeOptions, setEmployeeOptions] = useState<any[]>([]); // Employees dropdown options
	const [loadingEmployees, setLoadingEmployees] = useState<boolean>(false);
	const [loadingBorrowed, setLoadingBorrowed] = useState<boolean>(true);
	const [confirmModalOpen, setConfirmModalOpen] = useState(false); // Controls modal visibility
	const [employeeToRemove, setEmployeeToRemove] = useState<BorrowedEmployee | null>(null); // Employee to remove

	// Fetch borrowed employees and stores when the component is mounted
	useEffect(() => {
		const loadBorrowedEmployees = async () => {
			setStatusMessage('Loading borrowed employees...');
			try {
				const borrowedEmployeesData = await getBorrowedEmployees();
				setBorrowedEmployees(borrowedEmployeesData);
				setStatusMessage(null);
			} catch (error) {
				setErrorMessage('Failed to load borrowed employees');
				setStatusMessage(null);
			} finally {
				setLoadingBorrowed(false);
			}
		};

		const loadStores = async () => {
			setErrorMessage(null);
			setStatusMessage('Loading stores...');
			try {
				const storesData = await fetchStores();
				const dropdownOptions = mapStoresToDropdown(storesData);
				setStoreOptions(dropdownOptions);

				if (storesData.length > 0) {
					setMarket(storesData[0].marketName);
					setuserLogInStore(storesData[0].userLogInStore);
				}
				setStatusMessage(null);
			} catch (error) {
				setErrorMessage('Failed to load stores');
				setStatusMessage(null);
			} finally {
				setLoadingStores(false);
			}
		};

		loadBorrowedEmployees();
		loadStores();
	}, []);

	const handleStoreChange = async (value: string) => {
		setErrorMessage(null);
		setSelectedStore(value);
		setLoadingEmployees(true);
		setStatusMessage('Loading employees...');

		try {
			const employees = await fetchEmployees(value, market);
			const dropdownOptions = mapEmployeesToDropdown(employees);
			setEmployeeOptions(dropdownOptions);
			setStatusMessage(null);
		} catch (error) {
			setErrorMessage('Failed to load employees');
		} finally {
			setLoadingEmployees(false);
		}
	};

	const handleRemoveEmployee = async (borrowedEmployee: BorrowedEmployee) => {
		setErrorMessage(null);
		setStatusMessage('Removing borrowed employee...');
		try {
			// Call the deleteBorrowedEmployee API method
			const result = await deleteBorrowedEmployee(
				borrowedEmployee.lastName,
				borrowedEmployee.firstName,
				borrowedEmployee.payrollNumber,
				borrowedEmployee.loanStartDate,
				borrowedEmployee.loanEndDate,
			);

			if (result.statusCode == 0) {
				// Refresh the borrowed employees list after deletion
				const updatedBorrowedEmployees = await getBorrowedEmployees();
				setBorrowedEmployees(updatedBorrowedEmployees);

				setErrorMessage(null);
				setStatusMessage('Borrowed employee removed successfully.');
			}
			else {
				setErrorMessage(`Status code: [${result.statusCode}], status message: [${result.statusMessage}]`);
				setStatusMessage(null);
			}
		} catch (error) {
			setErrorMessage('Failed to remove borrowed employee');
			setStatusMessage(null);
		} finally {
			setConfirmModalOpen(false); // Close modal after processing
		}
	};

	// Open the confirmation modal
	const confirmRemoveEmployee = (borrowedEmployee: BorrowedEmployee) => {
		setEmployeeToRemove(borrowedEmployee); // Store the employee to remove
		setConfirmModalOpen(true); // Open modal
	};

	// Handle the actual removal when "Yes" is clicked in the modal
	const handleConfirmRemove = () => {
		if (employeeToRemove) {
			handleRemoveEmployee(employeeToRemove);
		}
	};

	const handleSubmit = async () => {
		if (!employee || !selectedStore || !startDate || !endDate) {
			setErrorMessage('All fields are required, including both dates.');
			return;
		}

		setStatusMessage('Submitting borrowed employee...');
		try {
			const result = await submitBorrowEmployee(selectedStore, employee, startDate, endDate);

			if (result.statusCode == 0) {
				// After submitting, refresh the borrowed employees list
				const updatedBorrowedEmployees = await getBorrowedEmployees();
				setBorrowedEmployees(updatedBorrowedEmployees);

				setErrorMessage(null);
				setStatusMessage('Borrowed employee submitted successfully.');
			}
			else {
				setErrorMessage(`Status code: [${result.statusCode}], status message: [${result.statusMessage}]`);
				setStatusMessage(null);
			}
		} catch (error) {
			setErrorMessage('Failed to submit borrowed employee');
			setStatusMessage(null);
		}
	};

	const handlePermanentBorrow = () => {
		setEndDate('2099-12-31'); // Set permanent borrow end date
	};

	return (
		<Container className="borrow-form-container">
			<Header as="h1" className="borrow-form-header">Microcon Employee Borrowing (MEB)</Header>
			<Header as="h2" className="borrow-form-header2">Store: {userLogInStore}</Header>

			<Segment padded>
				<Form size="large">
					<Grid stackable>
						<Grid.Row>
							<Grid.Column>
								<Form.Field>
									<Label className="borrow-form-label">Current Market:</Label>
									<Label style={{ fontWeight: 'bold', color: 'black' }}>{market}</Label>
								</Form.Field>
								<Form.Field>
									<Label className="borrow-form-label">Select Store:</Label>
									{loadingStores ? (
										<Loader active inline="centered" size="small">
											Loading stores...
										</Loader>
									) : (
										<Dropdown
											className="borrow-form-dropdown"
											placeholder="Select Store"
											fluid
											selection
											options={storeOptions}
											onChange={(e, { value }) => handleStoreChange(value as string)}
										/>
									)}
								</Form.Field>
							</Grid.Column>
						</Grid.Row>

						<Grid.Row>
							<Grid.Column>
								<Form.Field>
									<Label className="borrow-form-label">Select an employee to borrow:</Label>
									{loadingEmployees ? (
										<Loader active inline="centered" size="small">
											Loading employees...
										</Loader>
									) : (
										<Dropdown
											className="borrow-form-dropdown"
											placeholder="Select Employee"
											fluid
											selection
											options={employeeOptions}
											onChange={(e, { value }) => setEmployee(value as string)}
										/>
									)}
								</Form.Field>
							</Grid.Column>
						</Grid.Row>

						<Grid.Row columns={3}>
							<Grid.Column>
								<Form.Field>
									<Label className="borrow-form-label">Borrow start date:</Label>
									<input
										type="date"
										className="borrow-form-input"
										value={startDate || ''} // Allow empty value when startDate is null
										onChange={(e) => setStartDate(e.target.value || null)} // Set to null if no date selected
									/>
								</Form.Field>
							</Grid.Column>

							<Grid.Column>
								<Form.Field>
									<Label className="borrow-form-label">Borrow end date:</Label>
									<input
										type="date"
										className="borrow-form-input"
										value={endDate || ''} // Allow empty value when endDate is null
										onChange={(e) => setEndDate(e.target.value || null)} // Set to null if no date selected
									/>
								</Form.Field>
							</Grid.Column>

							<Grid.Column>
								<Button className="permanent-borrow-button" onClick={handlePermanentBorrow}>
									Permanent Borrow
								</Button>
							</Grid.Column>
						</Grid.Row>

						<Grid.Row centered>
							<Grid.Column width={16} textAlign="center">
								<Button color="green" className="submit-borrow-button" size="large" width="20%" onClick={handleSubmit}>
									Submit Borrow
								</Button>
							</Grid.Column>
						</Grid.Row>
					</Grid>

					{(errorMessage || statusMessage) && (
						<Message negative={!!errorMessage} positive={!!statusMessage} className="status-message">
							{errorMessage || statusMessage}
						</Message> )}
				</Form>
			</Segment>

			<Segment>
				<Header as="h3" textAlign="center">
					Borrowed Employees
				</Header>

				{loadingBorrowed ? (
					<Loader active inline="centered" size="small">
						Loading borrowed employees...
					</Loader>
				) : (
					<div className="borrowed-employees-table-container">
						<Table celled className="borrowed-employees-table">
							<Table.Header className="borrowed-employees-header">
								<Table.Row>
									<Table.HeaderCell>LastName</Table.HeaderCell>
									<Table.HeaderCell>FirstName</Table.HeaderCell>
									<Table.HeaderCell>POS File Number</Table.HeaderCell>
									<Table.HeaderCell>Loan Start Date</Table.HeaderCell>
									<Table.HeaderCell>Loan End Date</Table.HeaderCell>
									<Table.HeaderCell>Remove</Table.HeaderCell> {/* New Remove Column */}
								</Table.Row>
							</Table.Header>
							<Table.Body>
								{borrowedEmployees.map((borrowedEmployee, index) => (
									<Table.Row key={index}>
										<Table.Cell>{borrowedEmployee.lastName}</Table.Cell>
										<Table.Cell>{borrowedEmployee.firstName}</Table.Cell>
										<Table.Cell>{borrowedEmployee.payrollNumber}</Table.Cell>
										<Table.Cell>
											{borrowedEmployee.loanStartDate
												? new Date(borrowedEmployee.loanStartDate).toLocaleDateString('en-CA')
												: 'Invalid Date'}
										</Table.Cell>
										<Table.Cell>
											{borrowedEmployee.loanEndDate
												? new Date(borrowedEmployee.loanEndDate).toLocaleDateString('en-CA')
												: 'Invalid Date'}
										</Table.Cell>
										<Table.Cell>
											<Icon
												name="trash"
												color="red"
												style={{ cursor: 'pointer' }}
												onClick={() => confirmRemoveEmployee(borrowedEmployee)} // Call handleRemoveEmployee on click
											/>
										</Table.Cell>
									</Table.Row>
								))}
							</Table.Body>
						</Table>
					</div>
				)}
			</Segment>

			{/* Confirmation Modal */}
			<Modal
				open={confirmModalOpen}
				onClose={() => setConfirmModalOpen(false)} // Close modal on cancel
				size="small"
			>
				<Header icon="trash" content="Confirm Remove Employee" />
				<Modal.Content>
					<p>Are you sure you want to remove this employee?</p>
				</Modal.Content>
				<Modal.Actions>
					<Button color="red" onClick={() => setConfirmModalOpen(false)}>
						<Icon name="remove" /> No
					</Button>
					<Button color="green" onClick={handleConfirmRemove}>
						<Icon name="checkmark" /> Yes
					</Button>
				</Modal.Actions>
			</Modal>
		</Container>
	);
};

export default BorrowingForm;
